import { useQuery } from '@tanstack/react-query';
import {
  fetchClientServicePmVisits,
  serviceKeys,
  type ClientServiceAgreementRow,
} from '../../data/service';
import { runSupabaseQuery, safeErrorMessage } from '../../utils/appErrors';

export default function PmIntakeFields({
  clientId,
  branchId,
  date,
  agreements,
  value,
  onChange,
}: {
  clientId: string;
  branchId: string;
  date: string;
  agreements: ClientServiceAgreementRow[];
  value: string;
  onChange: (value: string) => void;
}) {
  const visits = useQuery({
    queryKey: serviceKeys.clientPmVisits(clientId),
    enabled: !!clientId,
    queryFn: () =>
      runSupabaseQuery(
        () => fetchClientServicePmVisits(clientId),
        'Unable to load PM visits',
        'service.pm',
      ),
  });
  const eligible = (visits.data ?? []).filter(
    (v) =>
      !v.generated_ticket_id &&
      !['completed', 'cancelled'].includes(v.status) &&
      agreements.some(
        (a) =>
          a.assignment_id === v.agreement_assignment_id &&
          a.status === 'active' &&
          (!a.client_branch_id || String(a.client_branch_id) === branchId) &&
          !!date &&
          a.start_date <= date &&
          a.end_date >= date,
      ),
  );
  return (
    <div className="service-readonly-field">
      <label className="flex items-center wrap gap-sm">
        <span>PM Visit</span>
        <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="none">Not a PM visit</option>
          <option value="paid">Paid PM visit</option>
          {eligible.map((v) => (
            <option key={v.pm_visit_id} value={v.pm_visit_id}>
              {v.tier?.name} / Visit {v.sequence_no} / Due {v.due_date}
            </option>
          ))}
          {!['none', 'paid'].includes(value) && !eligible.some((v) => v.pm_visit_id === value) ? (
            <option value={value}>Selected visit - check coverage date and location</option>
          ) : null}
        </select>
      </label>
      {value !== 'none' ? (
        <p className="text-sm">
          {value === 'paid'
            ? 'The standard PM catalog price applies.'
            : 'Scheduling uses one allotted visit and discounts the PM charge by 100%.'}{' '}
          Routine PM labor is included; extra work can be billed separately.
        </p>
      ) : null}
      {visits.error ? (
        <p role="alert">{safeErrorMessage(visits.error, 'Unable to load PM visits')}</p>
      ) : null}
    </div>
  );
}
